import { Icon } from '@mui/material';
import { ControlledMuiAutoCompleteSelect, ControlledTextField, IOptionType, PillButton } from '@zz2/zz2-ui';
import React, { useEffect, useMemo } from 'react';
import { IPackBillOfMaterialFormValues } from '../../../../../@types/model/masterData/packBillOfMaterial/packBillOfMaterialFormValues';
import { useAppSelector } from '../../../../../@types/redux';
import OptionType from '../../../../../@types/model/optionType';
import { useForm } from 'react-hook-form';
import PackBillOfMaterialModelHelper from '../../../../../@types/model/masterData/packBillOfMaterial/packBillOfMaterialModelHelper';
import { yupResolver } from '@hookform/resolvers/yup';
import CustomReactHookFormDevTools from '../../../../../components/devtools/ReactHookFormDevTools';

interface IPackBillOfMaterialEditFormProps {
    addNewMaterial : (values : IPackBillOfMaterialFormValues) => void;
}

const PackBillOfMaterialEditForm = (props : IPackBillOfMaterialEditFormProps) => {
    const materials = useAppSelector(x => x.masterData.materials);
    const packs = useAppSelector(x => x.masterData.packs);
    const unitOfMeasures = useAppSelector(x => x.masterData.unitOfMeasures);

    const initialFormValues = PackBillOfMaterialModelHelper.createFormValues(null, packs, materials, unitOfMeasures);

    const { control, formState: { isValid, isDirty }, getValues, setValue, trigger } = useForm<IPackBillOfMaterialFormValues>({
        defaultValues: initialFormValues,
        resolver: yupResolver(PackBillOfMaterialModelHelper.formSchema()),
        mode: 'onChange',
    });

    /*================================================================================================================
     *                                                  Queries
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        trigger();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const addNewMaterial = () => {
        const values = getValues();

        props.addNewMaterial(values);
    };

    const onMaterialChange = (selectedOption : IOptionType | null) : void => {
        const material = materials.find(x => x.id === selectedOption?.value);
        const currentlyActiveMaterialCost = material?.materialCosts.find(x => x.isActive && x.currentlyActive);

        setValue('pricePerUnit', currentlyActiveMaterialCost?.amount ?? 0, { shouldDirty: true, shouldValidate: true });
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const materialOptions = useMemo<Array<IOptionType>>(() => {
        return materials.filter(x => x.isActive).map(OptionType.fromDataModel);
    }, [materials]);

    const unitOfMeasureOptions = useMemo<Array<IOptionType>>(() => {
        return unitOfMeasures.filter(x => x.isActive).map((x) => {
            return { label: `(${x.code}) - ${x.name}`, value: x.id };
        });
    }, [unitOfMeasures]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <div className={'fdc wfill p20'}>
            <form className={'fdr mb15'}>
                <ControlledMuiAutoCompleteSelect
                    className={'flx1 pb5 mr20'}
                    name={'material'}
                    label={'Material'}
                    options={materialOptions}
                    defaultValues={initialFormValues.material}
                    control={control}
                    onChange={(value) => onMaterialChange(value as IOptionType | null)}
                />
                <ControlledMuiAutoCompleteSelect
                    className={'flx1 pb5 mr20'}
                    name={'unitOfMeasure'}
                    label={'Unit of Measure'}
                    options={unitOfMeasureOptions}
                    defaultValues={initialFormValues.material}
                    control={control}
                />
                <ControlledTextField
                    label={'Units'}
                    type={'number'}
                    name={'units'}
                    className={'flx1 mr20 mt8'}
                    control={control}
                    size={'small'}
                />
                <ControlledTextField
                    label={'Price Per Unit (in Rand)'}
                    name={'pricePerUnit'}
                    className={'flx1 mt8 mr20'}
                    control={control}
                    type={'number'}
                    size={'small'}
                    disabled
                />
                <PillButton
                    text={''}
                    icon={<Icon className={'cpd p0 m0'} fontSize={'large'}>
                                add_circle_outline
                    </Icon>}
                    color={'secondary'}
                    size={'small'}
                    className={'p0 mt10'}
                    disabled={ !isDirty || !isValid }
                    onClick={addNewMaterial}
                />
            </form>
            <CustomReactHookFormDevTools control={control} />
        </div>
    );
};

export default PackBillOfMaterialEditForm;